import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useGlobalStore } from '@/stores';
import { documentsApi } from '@/api';
import {
  OrderRelatedResponse,
  OrderResponse,
  OrderSummary,
} from '@/api/documents/documents.types.ts';
import { PaginatedResult } from '@/api/common/common.types.ts';

export const useGetOrder = (
  orderId: number,
  businessPartnerId?: number,
  options?: UseQueryOptions<OrderResponse, AxiosError>,
) => {
  const store = useGlobalStore();
  let _businessPartnerId = store.businessPartnerId as number;
  if (businessPartnerId != null) {
    _businessPartnerId = businessPartnerId;
  }

  return useQuery<OrderResponse, AxiosError>({
    queryKey: ['get-order', orderId, _businessPartnerId],
    queryFn: () =>
      documentsApi
        .getOrder(_businessPartnerId, orderId)
        .then((res) => res.data),
    ...options,
  });
};

export const useGetOrderRelated = (
  orderId: number,
  businessPartnerId?: number,
  options?: Partial<UseQueryOptions<OrderRelatedResponse, AxiosError>>,
) => {
  const store = useGlobalStore();
  let _businessPartnerId = store.businessPartnerId as number;
  if (businessPartnerId != null) {
    _businessPartnerId = businessPartnerId;
  }

  return useQuery<OrderRelatedResponse, AxiosError>({
    queryKey: ['get-order-related', orderId, _businessPartnerId],
    queryFn: () =>
      documentsApi
        .getOrderRelated(_businessPartnerId, orderId)
        .then((res) => res.data),
    ...options,
  });
};

export const useGetOrderSummaries = (
  page?: number,
  search?: string,
  sortBy?: string,
  sortDir?: 'asc' | 'desc',
  id?: number,
  options?: Partial<UseQueryOptions<PaginatedResult<OrderSummary>, AxiosError>>,
) => {
  const store = useGlobalStore();
  let businessPartnerId = store.businessPartnerId as number;
  if (id != null) {
    businessPartnerId = id;
  }

  return useQuery<PaginatedResult<OrderSummary>, AxiosError>({
    queryKey: [
      'order-summaries',
      page,
      businessPartnerId,
      search,
      sortBy,
      sortDir,
    ],
    queryFn: () =>
      documentsApi
        .getOrderSummaries(businessPartnerId, page ?? 1, search, sortBy, sortDir)
        .then((res) => res.data),
    ...options,
  });
};
